/**
 * useAgentCapabilities Hook
 * ==========================
 * Fetches capabilities attached to a specific agent.
 * Returns them as palette-compatible nodes for the "Agent Capabilities" section.
 * Supports real-time refresh via event bus.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '../../../config';

// Simple event bus for cross-component capability sync
const listeners = new Set();
export const emitCapabilityChange = (agentId) => {
  listeners.forEach(fn => fn(agentId));
};
export const onCapabilityChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useAgentCapabilities = (agentId) => {
  const [capabilities, setCapabilities] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchCapabilities = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);

    try {
      // Fetch attached capabilities
      const res = await fetch(`${BACKEND_URL}/api/unified-capabilities/agent-capabilities/${agentId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch agent capabilities');
      const data = await res.json();
      if (!mountedRef.current) return;
      setCapabilities(data.attached_capabilities || []);

      // Fetch resolved capabilities (with health status)
      const resolveRes = await fetch(`${BACKEND_URL}/api/unified-capabilities/resolve/${agentId}`, {
        credentials: 'include',
      });
      if (resolveRes.ok) {
        const resolveData = await resolveRes.json();
        if (mountedRef.current) {
          setResolved(resolveData.resolved_capabilities || []);
        }
      }
    } catch (err) {
      console.error('Error fetching agent capabilities:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [agentId]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchCapabilities();
    return () => { mountedRef.current = false; };
  }, [fetchCapabilities]);

  // Listen for capability change events
  useEffect(() => {
    const unsubscribe = onCapabilityChange((changedAgentId) => {
      if (!changedAgentId || changedAgentId === agentId) {
        fetchCapabilities();
      }
    });
    return unsubscribe;
  }, [agentId, fetchCapabilities]);

  // Merge attached list with resolved status
  const enriched = capabilities.map(cap => {
    const res = resolved.find(r => r.id === cap.capability_id);
    return {
      ...cap,
      auth: res?.auth || 'unknown',
      status: res?.status || 'unknown',
      actions: res?.actions || [],
      provider: res?.provider || cap.capability_id,
    };
  });

  return {
    capabilities: enriched,
    rawAttached: capabilities,
    resolved,
    loading,
    refresh: fetchCapabilities,
  };
};
